import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AuthModal from "@/components/AuthModal";
import Home from "@/pages/Home";
import Explore from "@/pages/Explore";
import QuestionDetail from "@/pages/QuestionDetail";
import ExploreBooks from "@/pages/explore/ExploreBooks";
import ExploreTypes from "@/pages/explore/ExploreTypes";
import FacetIndex from "@/pages/explore/FacetIndex";
import FacetPage from "@/pages/explore/FacetPage";
import { ThemeGate } from "@/components/ThemeGate";
import References from "@/pages/References";
import Read from "@/pages/Read";
import Dashboard from "@/pages/Dashboard";
import AccountSettings from "@/pages/AccountSettings";
import SearchPage from "@/pages/SearchPage";
import Journal from "@/pages/Journal";
import Highlights from "@/pages/Highlights";
import CommentaryHome from "@/pages/commentary/CommentaryHome";
import CommentaryBook from "@/pages/commentary/CommentaryBook";
import CommentaryChapter from "@/pages/commentary/CommentaryChapter";
import CommentarySearch from "@/pages/commentary/CommentarySearch";
import About from "@/pages/About";
import DailySpark from "@/pages/DailySpark";
import Book from "@/pages/Book";
import Type from "@/pages/Type";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import { RefTaggerLoader } from "@/components/RefTagger";
import ScrollToTop from "@/components/ScrollToTop";
import PageViewTracker from "@/components/PageViewTracker";
import PageTransition from "@/components/motion/PageTransition";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import Contact from "@/pages/Contact";
import ResetPassword from "@/pages/ResetPassword";
import BlogHome from "@/pages/blog/BlogHome";
import BlogPost from "@/pages/blog/BlogPost";
import BlogWrite from "@/pages/blog/BlogWrite";
import { BlogMine, BlogAuthor } from "@/pages/blog/BlogPeople";
import SmoothScroll from "@/components/motion/SmoothScroll";

function AppShell() {
  const location = useLocation();
  // Process OAuth return BEFORE anything else (synchronous, avoids race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="App flex min-h-screen flex-col bg-background">
      <ScrollToTop />
      <PageViewTracker />
      <Header />
      <SmoothScroll />
      <main className="flex flex-1 flex-col">
        <RouteErrorBoundary resetKey={location.pathname}>
        <PageTransition>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/daily" element={<DailySpark />} />
          <Route path="/question/:id" element={<QuestionDetail />} />
          <Route path="/admin/preview/:id" element={<QuestionDetail preview />} />
          <Route path="/book/:book" element={<Book />} />
          <Route path="/type/:type" element={<Type />} />
          <Route path="/explore/books" element={<ExploreBooks />} />
          <Route path="/explore/types" element={<ExploreTypes />} />
          <Route path="/explore/:facet" element={<ThemeGate><FacetIndex /></ThemeGate>} />
          <Route path="/explore/:facet/:name" element={<ThemeGate><FacetPage /></ThemeGate>} />
          <Route path="/references" element={<References />} />
          <Route path="/read" element={<Read />} />
          <Route path="/read/:book/:chapter" element={<Read />} />
          <Route path="/browse" element={<Navigate to="/explore/people" replace />} />
          <Route path="/explore/who-asked" element={<Navigate to="/explore/people" replace />} />
          <Route path="/explore/asked-to" element={<Navigate to="/explore/people" replace />} />
          <Route path="/explore/who-asked/:name" element={<LegacyPerson role="asked" />} />
          <Route path="/explore/asked-to/:name" element={<LegacyPerson role="received" />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/account" element={<AccountSettings />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:id" element={<Journal />} />
          <Route path="/highlights" element={<Highlights />} />
          <Route path="/commentary" element={<CommentaryHome />} />
          <Route path="/commentary/:slug" element={<CommentaryHome />} />
          <Route path="/commentary/:slug/search" element={<CommentarySearch />} />
          <Route path="/commentary/:slug/:book" element={<CommentaryBook />} />
          <Route path="/commentary/:slug/:book/:chapter" element={<CommentaryChapter />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/blog" element={<BlogHome />} />
          <Route path="/blog/write" element={<BlogWrite />} />
          <Route path="/blog/edit/:id" element={<BlogWrite />} />
          <Route path="/blog/mine" element={<BlogMine />} />
          <Route path="/blog/author/:id" element={<BlogAuthor />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
        </PageTransition>
        </RouteErrorBoundary>
      </main>
      <Footer />
      <AuthModal />
      <RefTaggerLoader />
    </div>
  );
}

function LegacyPerson({ role }) {
  const { name } = useParams();
  return <Navigate to={`/explore/people/${encodeURIComponent(name)}?role=${role}`} replace />;
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppShell />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
